import _defineProperty from "/app/node_modules/.pnpm/next@13.4.17_@babel+core@7.14.3_@opentelemetry+api@1.4.1_react-dom@18.2.0_react@18.2.0_sass@1.49.9/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Cell, Icon, Divider, Spacer, Tabs, Text } from '@vinted/web-ui';
import { InfoCircle24 } from '@vinted/monochrome-icons';
import classNames from 'classnames';
import { FormattedMessage } from 'components/@support';
import { ShowAtBreakpoint } from 'components/Breakpoint';
import { PushUpCheckout } from 'components/Bumps';
import { ClosetPromotionCheckout } from 'components/ClosetPromotion';
import InfoBanner from 'components/InfoBanner';
import SeparatedList from 'components/SeparatedList';
import useAbTest from 'hooks/useAbTest';
import useBreakpoint from 'hooks/useBreakpoint';
import useLocation from 'hooks/useLocation';
import useTracking from 'hooks/useTracking';
import useTranslate from 'hooks/useTranslate';
import useMultipleCollectionsAbTest from 'components/FeaturedCollection/useMultipleCollectionsAbTest';
import { ClickableElement } from 'constants/tracking/clickable-elements';
import { Screen } from 'constants/tracking/screens';
import { viewScreenEvent, viewUserEvent, clickEvent } from 'libs/common/event-tracker/events';
import { isValueInObject } from 'libs/utils/object';
import { toUrlQuery } from 'libs/utils/url';
import { reloadPage } from 'libs/utils/window';
import { getBanners } from 'state/banners/selectors';
import { getIsFeatureSwitchEnabled } from 'state/feature-switches/selectors';
import { getPromotedItemId } from 'state/profile/selectors';
import { actions as profileActions } from 'state/profile/slice';
import { getUser } from 'state/session/selectors';
import { getVasGalleryAbTest } from 'state/vas-gallery/selectors';
import PromotionalToolsPropositionModal from 'components/VasGallery/PromotionalToolsPropositionModal';
import { BusinessAccountBanner, PoliciesModal } from './BusinessAccount';
import InfoHeader from './InfoHeader';
import ProfileBanners from './ProfileBanners';
import ProfileCollections from './ProfileCollections';
import ProfileFeedback from './ProfileFeedback';
import ProfileFetcher from './ProfileFetcher';
import ProfileAdminSection from './ProfileAdminSection';
import MultipleCollectionsBlock from './MultipleCollectionsBlock';
import ProfileInfo from './ProfileInfo';
import ProfileItemPromoteModal from './ProfileItemPromoteModal';
import PromosAfterItemUpload from './PromosAfterItemUpload';
import UserItems from './UserItems';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MIN_BUNDLE_ITEMS = 2;
var TabType;

(function (TabType) {
  TabType["Closet"] = "closet";
  TabType["Feedback"] = "feedback";
  TabType["About"] = "about";
})(TabType || (TabType = {}));

var TabClickableElements;

(function (TabClickableElements) {
  TabClickableElements[TabClickableElements["closet"] = ClickableElement.ProfileTab] = "closet";
  TabClickableElements[TabClickableElements["feedback"] = ClickableElement.FeedbackTab] = "feedback";
  TabClickableElements[TabClickableElements["about"] = ClickableElement.AboutTab] = "about";
})(TabClickableElements || (TabClickableElements = {}));

const isTabType = tabType => isValueInObject(tabType, TabType);

const Profile = _ref => {
  let {
    userId,
    viewedCollectionId,
    nextPush
  } = _ref;
  const breakpoints = useBreakpoint();
  const dispatch = useDispatch();
  const {
    replaceHistoryState,
    relativeUrl,
    searchParams
  } = useLocation();
  const {
    track
  } = useTracking();
  const bundleTranslate = useTranslate('bundles');
  const vasGalleryTranslate = useTranslate('vas_gallery');
  const defaultActiveTab = isTabType(searchParams.tab) ? searchParams.tab : TabType.Closet;
  const currentUser = useSelector(getUser);
  const banners = useSelector(getBanners);
  const vasGalleryAbTest = useSelector(getVasGalleryAbTest);
  const isVasGalleryFeatureDisabled = useSelector(getIsFeatureSwitchEnabled('disable_vas_gallery_orders'));
  const isVasGalleryEnabled = vasGalleryAbTest.visible && !isVasGalleryFeatureDisabled;
  const isNextPageMemberEnabled = useSelector(getIsFeatureSwitchEnabled('next_page_member'));
  const isProfileViewUserTrackingEnabled = useSelector(getIsFeatureSwitchEnabled('profile_view_user_tracking'));
  const isHideAdminUserCommentEnabled = useSelector(getIsFeatureSwitchEnabled('hide_admin_user_comment'));
  const isContentTranslationEnabled = useSelector(getIsFeatureSwitchEnabled('content_translation_web'));
  const isPortalMergeSourceEnabled = useSelector(getIsFeatureSwitchEnabled('portal_merge_source'));
  const isPromoteModalOpen = useSelector(getPromotedItemId) !== null;
  const isMultipleCollectionsEnabled = useMultipleCollectionsAbTest();
  const {
    0: activeTabId,
    1: setActiveTabId
  } = useState(defaultActiveTab);
  const {
    0: isPoliciesModalOpen,
    1: setIsPoliciesModalOpen
  } = useState(!!searchParams.policy);
  const {
    0: isPromotionalToolsModalOpen,
    1: setIsPromotionalToolsModalOpen
  } = useState(false);
  const allowBump = !isPortalMergeSourceEnabled;
  const isViewingSelf = (currentUser === null || currentUser === void 0 ? void 0 : currentUser.id) === userId;
  const screenName = isViewingSelf ? Screen.CurrentUserProfile : Screen.UserProfile;
  const handleChangeTab = useCallback(tabId => {
    setActiveTabId(tabId);
    replaceHistoryState("".concat(relativeUrl, "?").concat(toUrlQuery({
      tab: tabId
    })));
  }, [relativeUrl, replaceHistoryState]);
  useAbTest({
    abTestName: 'vas_gallery_iteration1_v2',
    shouldTrackExpose: true
  });
  useEffect(() => {
    if (!(isNextPageMemberEnabled && isProfileViewUserTrackingEnabled)) return;
    const refererPath = document.referrer ? new URL(document.referrer).pathname : 'unknown';
    track(viewUserEvent({
      path: refererPath,
      user_id: userId
    }));
  }, [isNextPageMemberEnabled, isProfileViewUserTrackingEnabled, track, userId]);
  useEffect(() => {
    track(viewScreenEvent({
      screen: screenName
    }));
  }, [screenName, track]);
  useEffect(() => {
    if (isPromotionalToolsModalOpen) {
      track(clickEvent({
        target: ClickableElement.VasInfo,
        screen: Screen.CurrentUserProfile
      }));
    }
  }, [isPromotionalToolsModalOpen, track]);
  useEffect(() => {
    if ([TabType.Closet, TabType.Feedback].includes(activeTabId)) return;
    if (breakpoints.phones && activeTabId === TabType.About) return;
    handleChangeTab(TabType.Closet);
  }, [activeTabId, breakpoints.phones, handleChangeTab]);

  const onTabChange = tab => {
    // TODO: Remove casting
    handleChangeTab(tab.id);
    if (isViewingSelf) track(clickEvent({
      screen: Screen.CurrentUserProfile,
      target: TabClickableElements[tab.id]
    }));
  };

  const changeTab = tabId => () => {
    handleChangeTab(tabId);
  };

  const handleExtraServiceCheckoutSuccess = reloadPage;

  const handlePromoteModalClose = () => {
    dispatch(profileActions.setPromotedItemId({
      id: null
    }));
  };

  const togglePoliciesModal = () => {
    setIsPoliciesModalOpen(prevState => !prevState);
  };

  const togglePromotionalToolsModal = () => {
    setIsPromotionalToolsModalOpen(prevState => !prevState);
  };

  const getTabs = () => {
    const tabs = [{
      id: TabType.Closet,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        id: "profile.tabs.closet"
      })
    }, {
      id: TabType.Feedback,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        id: "profile.tabs.feedback"
      })
    }];

    if (breakpoints.phones) {
      tabs.push({
        id: TabType.About,
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          id: "profile.tabs.about"
        })
      });
    }

    return tabs;
  };

  const renderWarningBlock = keyId => /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Cell, {
      body: /*#__PURE__*/_jsx(FormattedMessage, {
        id: keyId
      }),
      theme: "warning"
    }), /*#__PURE__*/_jsx(Spacer, {
      size: Spacer.Size.Large
    })]
  });

  const renderItemsWarning = user => {
    if (!user) return null;
    const isViewedByGod = currentUser === null || currentUser === void 0 ? void 0 : currentUser.is_god;
    const isViewedByModerator = currentUser === null || currentUser === void 0 ? void 0 : currentUser.moderator;
    if (!(isViewingSelf || isViewedByGod || isViewedByModerator)) return null;

    if (user.has_replica_proof_items) {
      return renderWarningBlock('profile.notification.replica_warning');
    }

    if (!user.has_item_alerts) return null;
    return renderWarningBlock('profile.notification.items_warning');
  };

  const renderClosetTab = profileProps => {
    const {
      user,
      userInfo,
      showActions,
      items,
      collections,
      isLoading,
      itemsCount,
      totalItemsCount,
      filters,
      catalogs: profileCatalogs,
      fetchMoreItems,
      updateItems,
      updateFilters
    } = profileProps;

    const getBundleDiscountText = () => {
      const bundleDiscount = user === null || user === void 0 ? void 0 : user.bundle_discount;

      if (!(bundleDiscount !== null && bundleDiscount !== void 0 && bundleDiscount.enabled)) {
        return bundleTranslate('discounts.not_available');
      }

      const fraction = Math.max(...bundleDiscount.discounts.map(discount => +discount.fraction));
      return bundleTranslate('discounts.available_up_to', {
        percentage: Math.round(fraction * 100)
      });
    };

    const bundleUrl = "".concat(relativeUrl, "/bundles/new");
    const showBundles = totalItemsCount >= MIN_BUNDLE_ITEMS && showActions && !!(user !== null && user !== void 0 && user.can_bundle);
    const bundleBannerProps = {
      url: bundleUrl,
      subtitle: getBundleDiscountText(),
      showBundles
    };

    if (breakpoints.tabletsUp) {
      return /*#__PURE__*/_jsxs("div", {
        className: classNames('profile__items-wrapper'),
        children: [/*#__PURE__*/_jsxs("div", {
          children: [/*#__PURE__*/_jsx(InfoBanner, {
            screen: Screen.ItemUpload
          }), isViewingSelf && isVasGalleryEnabled && /*#__PURE__*/_jsx(Button, {
            inline: true,
            iconColor: Icon.Color.GreyscaleLevel3,
            iconName: InfoCircle24,
            iconPosition: Button.IconPosition.Right,
            onClick: togglePromotionalToolsModal,
            styling: Button.Styling.Flat,
            testId: "promotional_tools_link",
            theme: "muted",
            children: /*#__PURE__*/_jsx(Text, {
              type: Text.Type.Subtitle,
              text: vasGalleryTranslate('promotional_tools_link')
            })
          }), /*#__PURE__*/_jsx(Spacer, {
            size: Spacer.Size.Medium
          }), /*#__PURE__*/_jsx(PromotionalToolsPropositionModal, {
            show: isPromotionalToolsModalOpen,
            onConfirm: togglePromotionalToolsModal
          }), /*#__PURE__*/_jsx(ProfileBanners, {
            user: user,
            bundleProps: bundleBannerProps,
            showActions: showActions,
            viewingSelf: isViewingSelf
          }), !isMultipleCollectionsEnabled && /*#__PURE__*/_jsx(ProfileCollections, {
            collections: collections,
            viewingSelf: isViewingSelf,
            allowBump: allowBump
          }), isMultipleCollectionsEnabled && /*#__PURE__*/_jsx(MultipleCollectionsBlock, {
            viewingSelf: isViewingSelf,
            userId: userId,
            viewedCollectionId: viewedCollectionId,
            nextPush: nextPush
          }), /*#__PURE__*/_jsx(PushUpCheckout, {
            isInitiatedFromMultiSelect: false,
            onCheckoutSuccess: handleExtraServiceCheckoutSuccess
          }), /*#__PURE__*/_jsx(ClosetPromotionCheckout, {
            onCheckoutSuccess: handleExtraServiceCheckoutSuccess
          })]
        }), /*#__PURE__*/_jsx(UserItems, {
          userId: userId,
          allowBump: true,
          userOnHoliday: !!(user !== null && user !== void 0 && user.is_on_holiday),
          currentUserId: currentUser === null || currentUser === void 0 ? void 0 : currentUser.id,
          items: items,
          itemsCount: itemsCount,
          isLoading: isLoading,
          filters: filters,
          catalogs: profileCatalogs,
          fetchMore: fetchMoreItems,
          update: updateItems,
          updateFilters: updateFilters,
          totalItemsCount: totalItemsCount
        })]
      });
    }

    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx("div", {
        className: "u-ui-margin-large",
        children: /*#__PURE__*/_jsx(InfoBanner, {
          screen: screenName
        })
      }), /*#__PURE__*/_jsxs("div", {
        children: [renderItemsWarning(user), /*#__PURE__*/_jsxs(SeparatedList, {
          separator: /*#__PURE__*/_jsx(Divider, {}),
          children: [banners.businessAccount && isViewingSelf && /*#__PURE__*/_jsx(BusinessAccountBanner, {
            banner: banners.businessAccount
          }), user && /*#__PURE__*/_jsx(ProfileInfo, _objectSpread(_objectSpread({
            user: user
          }, userInfo), {}, {
            showActions: showActions,
            isAvailableForMigration: isPortalMergeSourceEnabled,
            viewingSelf: isViewingSelf,
            onTogglePoliciesModal: togglePoliciesModal,
            screen: Screen.UserProfile,
            compactMode: true,
            onUserInfoClick: changeTab(TabType.Feedback),
            isContentTranslationEnabled: isContentTranslationEnabled
          })), isViewingSelf && isVasGalleryEnabled && /*#__PURE__*/_jsx(Button, {
            inline: true,
            iconColor: Icon.Color.GreyscaleLevel3,
            iconName: InfoCircle24,
            iconPosition: Button.IconPosition.Right,
            onClick: togglePromotionalToolsModal,
            styling: Button.Styling.Flat,
            testId: "promotional_tools_link",
            theme: "muted",
            children: /*#__PURE__*/_jsx(Text, {
              type: Text.Type.Subtitle,
              text: vasGalleryTranslate('promotional_tools_link')
            })
          }), /*#__PURE__*/_jsx(Spacer, {
            size: Spacer.Size.Medium
          }), /*#__PURE__*/_jsx(PromotionalToolsPropositionModal, {
            show: isPromotionalToolsModalOpen,
            onConfirm: togglePromotionalToolsModal
          }), /*#__PURE__*/_jsx(ProfileBanners, {
            user: user,
            bundleProps: bundleBannerProps,
            showActions: showActions,
            viewingSelf: isViewingSelf
          })]
        }), /*#__PURE__*/_jsx(Divider, {}), !isMultipleCollectionsEnabled && /*#__PURE__*/_jsx(ProfileCollections, {
          collections: collections,
          viewingSelf: isViewingSelf,
          allowBump: allowBump
        }), isMultipleCollectionsEnabled && /*#__PURE__*/_jsx(MultipleCollectionsBlock, {
          viewingSelf: isViewingSelf,
          userId: userId,
          viewedCollectionId: viewedCollectionId,
          nextPush: nextPush
        }), /*#__PURE__*/_jsx(PushUpCheckout, {
          isInitiatedFromMultiSelect: false,
          onCheckoutSuccess: handleExtraServiceCheckoutSuccess
        }), /*#__PURE__*/_jsx(ClosetPromotionCheckout, {
          onCheckoutSuccess: handleExtraServiceCheckoutSuccess
        })]
      }), /*#__PURE__*/_jsx(UserItems, {
        userId: userId,
        allowBump: true,
        userOnHoliday: !!(user !== null && user !== void 0 && user.is_on_holiday),
        currentUserId: currentUser === null || currentUser === void 0 ? void 0 : currentUser.id,
        items: items,
        itemsCount: itemsCount,
        isLoading: isLoading,
        filters: filters,
        catalogs: profileCatalogs,
        fetchMore: fetchMoreItems,
        update: updateItems,
        updateFilters: updateFilters,
        totalItemsCount: totalItemsCount
      })]
    });
  };

  const renderAboutTab = profileProps => {
    if (!profileProps.user) return null;
    return /*#__PURE__*/_jsx(ProfileInfo, _objectSpread(_objectSpread({
      user: profileProps.user
    }, profileProps.userInfo), {}, {
      showActions: profileProps.showActions,
      isAvailableForMigration: isPortalMergeSourceEnabled,
      viewingSelf: isViewingSelf,
      onTogglePoliciesModal: togglePoliciesModal,
      screen: Screen.UserAbout,
      onUserInfoClick: changeTab(TabType.Feedback),
      isContentTranslationEnabled: isContentTranslationEnabled
    }));
  };

  const renderFeedbackTab = profileProps => {
    if (!profileProps.user) return null;
    return /*#__PURE__*/_jsx(ProfileFeedback, _objectSpread(_objectSpread({}, profileProps.userInfo.feedbackProps), {}, {
      isContentTranslationEnabled: isContentTranslationEnabled,
      userId: profileProps.user.id,
      username: profileProps.user.login,
      showTitle: false
    }));
  };

  const renderActiveTab = profileProps => {
    switch (activeTabId) {
      case TabType.Closet:
        return renderClosetTab(profileProps);

      case TabType.Feedback:
        return renderFeedbackTab(profileProps);

      case TabType.About:
        return renderAboutTab(profileProps);

      default:
        return null;
    }
  };

  const renderPoliciesModal = user => {
    if (!(user !== null && user !== void 0 && user.business)) return null;
    return /*#__PURE__*/_jsx(PoliciesModal, {
      show: isPoliciesModalOpen,
      onClose: togglePoliciesModal,
      user: user
    });
  };

  const buildProfileRenderer = profileProps => {
    const {
      user,
      userInfo,
      showActions
    } = profileProps;
    const {
      showSupportFunctionality
    } = userInfo;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [user && showSupportFunctionality && isHideAdminUserCommentEnabled && /*#__PURE__*/_jsx(ProfileAdminSection, {
        userId: user.id
      }), /*#__PURE__*/_jsxs(ShowAtBreakpoint, {
        breakpoint: "phones",
        children: [/*#__PURE__*/_jsx(Tabs, {
          items: getTabs(),
          width: Tabs.Width.Parent,
          activeItemId: activeTabId,
          onClick: onTabChange
        }), /*#__PURE__*/_jsx(Divider, {}), renderActiveTab(profileProps)]
      }), /*#__PURE__*/_jsx(ShowAtBreakpoint, {
        breakpoint: "tabletsUp",
        children: /*#__PURE__*/_jsx("div", {
          className: "container",
          children: /*#__PURE__*/_jsxs("div", {
            className: "profile u-flex-direction-column",
            children: [/*#__PURE__*/_jsxs("div", {
              children: [renderItemsWarning(user), banners.businessAccount && isViewingSelf && /*#__PURE__*/_jsxs(_Fragment, {
                children: [/*#__PURE__*/_jsx(BusinessAccountBanner, {
                  banner: banners.businessAccount
                }), /*#__PURE__*/_jsx(Spacer, {
                  size: Spacer.Size.X3Large
                })]
              }), user && /*#__PURE__*/_jsx(InfoHeader, _objectSpread(_objectSpread({
                user: user
              }, userInfo), {}, {
                showActions: showActions,
                isAvailableForMigration: isPortalMergeSourceEnabled,
                viewingSelf: isViewingSelf,
                screen: screenName,
                onTogglePoliciesModal: togglePoliciesModal,
                onRatingClick: changeTab(TabType.Feedback),
                isContentTranslationEnabled: isContentTranslationEnabled
              })), /*#__PURE__*/_jsx(Spacer, {
                orientation: Spacer.Orientation.Vertical,
                size: Spacer.Size.Large
              }), /*#__PURE__*/_jsx("span", {
                id: "tabs",
                children: /*#__PURE__*/_jsx(Tabs, {
                  items: getTabs(),
                  activeItemId: activeTabId,
                  onClick: onTabChange
                })
              }), /*#__PURE__*/_jsx(Divider, {})]
            }), activeTabId !== TabType.Feedback && /*#__PURE__*/_jsx(Spacer, {}), renderActiveTab(profileProps)]
          })
        })
      }), /*#__PURE__*/_jsx(PromosAfterItemUpload, {}), isVasGalleryEnabled && /*#__PURE__*/_jsx(ProfileItemPromoteModal, {
        showInlineVasGalleryCheckout: true,
        show: isPromoteModalOpen,
        onClose: handlePromoteModalClose,
        onCheckoutSuccess: handleExtraServiceCheckoutSuccess
      }), renderPoliciesModal(user)]
    });
  };

  return /*#__PURE__*/_jsx(ProfileFetcher, {
    userId: userId,
    children: buildProfileRenderer
  });
};

export default Profile;